import * as THREE from 'three';
import {
  FLOORPLAN_TEXTURE_CATALOG,
  getTextureCatalogEntry,
} from './floorPlanTextureCatalog.js';
import {
  DEFAULT_FLOOR_TEXTURE_ID,
  DEFAULT_WALL_TEXTURE_ID,
} from './floorPlanMaterialConfig.js';
import {
  applyBrightnessToThreeColor,
  getWallBrightnessMultiplier,
} from './floorPlanWallBrightness.js';

const FLOOR_COLOR = '#b8e6b0';
const WALL_COLOR = '#f4f1e8';
const ARCHITECTURAL_WALL_ROUGHNESS = 0.8;
const ARCHITECTURAL_WALL_TEXTURE_ROUGHNESS = 0.72;
const PURE_WHITE_WALL_EMISSIVE_INTENSITY = 0.06;

/**
 * @param {string | undefined | null} colorHex
 * @returns {boolean}
 */
function isPureWhiteWallColor(colorHex) {
  return typeof colorHex === 'string' && colorHex.trim().toLowerCase() === '#ffffff';
}

/** @typedef {{ loaded: boolean; floor: THREE.Texture | null; wallInside: THREE.Texture | null; wallOutside: THREE.Texture | null; byId: Map<string, THREE.Texture> }} BaseTextureBundle */

/** @type {Promise<BaseTextureBundle> | null} */
let loadPromise = null;

/** @type {BaseTextureBundle | null} */
let cachedBaseTextures = null;

/**
 * @param {string} url
 * @returns {Promise<THREE.Texture>}
 */
function loadTexture(url) {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      url,
      (texture) => {
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        resolve(texture);
      },
      undefined,
      reject,
    );
  });
}

/**
 * Load floor/wall textures once and cache for the app session.
 * @returns {Promise<BaseTextureBundle>}
 */
export function loadFloorPlanTextures() {
  if (cachedBaseTextures) {
    return Promise.resolve(cachedBaseTextures);
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = Promise.all(
    FLOORPLAN_TEXTURE_CATALOG.map(async (entry) => {
      try {
        const texture = await loadTexture(entry.source);
        return [entry.id, texture];
      } catch (err) {
        console.warn(`[FloorPlan3D] Failed to load texture ${entry.id}`, err);
        return [entry.id, null];
      }
    }),
  )
    .then((entries) => {
      /** @type {Map<string, THREE.Texture>} */
      const byId = new Map();
      entries.forEach(([id, texture]) => {
        if (texture) byId.set(id, texture);
      });

      cachedBaseTextures = {
        loaded: byId.size > 0,
        floor: byId.get(DEFAULT_FLOOR_TEXTURE_ID) ?? byId.get('floor-default') ?? null,
        wallInside: byId.get(DEFAULT_WALL_TEXTURE_ID) ?? byId.get('wall-inside-default') ?? null,
        wallOutside: byId.get('wall-outside-default') ?? null,
        byId,
      };
      return cachedBaseTextures;
    })
    .catch((err) => {
      console.warn('[FloorPlan3D] Texture load failed, using solid-color fallback.', err);
      cachedBaseTextures = {
        loaded: false,
        floor: null,
        wallInside: null,
        wallOutside: null,
        byId: new Map(),
      };
      loadPromise = null;
      return cachedBaseTextures;
    });

  return loadPromise;
}

/**
 * Clone a shared base texture so each material owns a disposable map reference.
 * @param {THREE.Texture} texture
 * @returns {THREE.Texture}
 */
export function cloneTextureForMaterial(texture) {
  const clone = texture.clone();
  clone.wrapS = THREE.RepeatWrapping;
  clone.wrapT = THREE.RepeatWrapping;
  clone.colorSpace = THREE.SRGBColorSpace;
  clone.needsUpdate = true;
  return clone;
}

/**
 * @param {string | null | undefined} textureId
 * @param {BaseTextureBundle | null | undefined} bundle
 * @returns {THREE.Texture | null}
 */
export function getCatalogTextureById(textureId, bundle) {
  if (!textureId || !bundle?.loaded) return null;
  const entry = getTextureCatalogEntry(textureId);
  if (!entry) return null;
  const base = bundle.byId?.get(entry.id) ?? null;
  return base ? cloneTextureForMaterial(base) : null;
}

/**
 * @typedef {Object} FloorPlanMaterialSet
 * @property {boolean} loaded
 * @property {THREE.MeshStandardMaterial} floor
 * @property {THREE.MeshStandardMaterial} wallInside
 * @property {THREE.MeshStandardMaterial} wallOutside
 * @property {THREE.MeshStandardMaterial} wallInsideDark
 * @property {THREE.MeshStandardMaterial} wallOutsideDark
 * @property {THREE.MeshStandardMaterial} floorFallback
 * @property {THREE.MeshStandardMaterial} wallInsideFallback
 * @property {THREE.MeshStandardMaterial} wallOutsideFallback
 * @property {THREE.MeshStandardMaterial} wallInsideDarkFallback
 * @property {THREE.MeshStandardMaterial} wallOutsideDarkFallback
 */

/**
 * @param {{ map?: THREE.Texture | null; colorHex?: string }} [options]
 * @returns {THREE.MeshStandardMaterial}
 */
export function createFloorSurfaceMaterial({ map = null, colorHex = FLOOR_COLOR } = {}) {
  return new THREE.MeshStandardMaterial({
    map: map ?? null,
    color: colorHex,
    side: THREE.DoubleSide,
    shadowSide: THREE.DoubleSide,
    roughness: map ? 0.6 : 0.75,
    metalness: 0,
  });
}

/**
 * Shared architectural wall surface material — aligned with floor light response.
 * @param {{
 *   map?: THREE.Texture | null;
 *   colorHex?: string;
 *   applyDarkTint?: boolean;
 * }} [options]
 * @returns {THREE.MeshStandardMaterial}
 */
export function createArchitecturalWallMaterial({
  map = null,
  colorHex = '#ffffff',
  applyDarkTint = false,
} = {}) {
  const color = new THREE.Color(colorHex);
  if (applyDarkTint) {
    applyBrightnessToThreeColor(color, getWallBrightnessMultiplier('dark'));
  }

  const material = new THREE.MeshStandardMaterial({
    map: map ?? null,
    color,
    side: THREE.DoubleSide,
    roughness: map ? ARCHITECTURAL_WALL_TEXTURE_ROUGHNESS : ARCHITECTURAL_WALL_ROUGHNESS,
    metalness: 0,
  });

  if (!map && isPureWhiteWallColor(colorHex)) {
    material.emissive.set('#ffffff');
    material.emissiveIntensity = PURE_WHITE_WALL_EMISSIVE_INTENSITY;
  }

  return material;
}

/**
 * @param {BaseTextureBundle | null | undefined} bundle
 * @returns {FloorPlanMaterialSet}
 */
export function createFloorPlanMaterials(bundle) {
  const floorFallback = createFloorSurfaceMaterial({ colorHex: FLOOR_COLOR });
  const wallInsideFallback = createArchitecturalWallMaterial({ colorHex: WALL_COLOR });
  const wallOutsideFallback = createArchitecturalWallMaterial({ colorHex: WALL_COLOR });
  const wallInsideDarkFallback = createArchitecturalWallMaterial({
    colorHex: '#ffffff',
    applyDarkTint: true,
  });
  const wallOutsideDarkFallback = createArchitecturalWallMaterial({
    colorHex: '#ffffff',
    applyDarkTint: true,
  });

  if (!bundle?.loaded || !bundle.floor || !bundle.wallInside || !bundle.wallOutside) {
    return {
      loaded: false,
      floor: floorFallback,
      wallInside: wallInsideFallback,
      wallOutside: wallOutsideFallback,
      wallInsideDark: wallInsideDarkFallback,
      wallOutsideDark: wallOutsideDarkFallback,
      floorFallback,
      wallInsideFallback,
      wallOutsideFallback,
      wallInsideDarkFallback,
      wallOutsideDarkFallback,
    };
  }

  const floorMap = cloneTextureForMaterial(bundle.floor);
  const wallInsideMap = cloneTextureForMaterial(bundle.wallInside);
  const wallOutsideMap = cloneTextureForMaterial(bundle.wallOutside);

  return {
    loaded: true,
    floor: createFloorSurfaceMaterial({ map: floorMap, colorHex: '#ffffff' }),
    wallInside: createArchitecturalWallMaterial({ map: wallInsideMap, colorHex: '#ffffff' }),
    wallOutside: createArchitecturalWallMaterial({ map: wallOutsideMap, colorHex: '#ffffff' }),
    wallInsideDark: createArchitecturalWallMaterial({
      map: wallInsideMap,
      colorHex: '#ffffff',
      applyDarkTint: true,
    }),
    wallOutsideDark: createArchitecturalWallMaterial({
      map: wallOutsideMap,
      colorHex: '#ffffff',
      applyDarkTint: true,
    }),
    floorFallback,
    wallInsideFallback,
    wallOutsideFallback,
    wallInsideDarkFallback,
    wallOutsideDarkFallback,
  };
}

/**
 * @param {FloorPlanMaterialSet} materials
 * @param {'inside' | 'outside'} classification
 * @param {'normal' | 'dark'} [shadeVariant]
 * @returns {THREE.MeshStandardMaterial}
 */
export function pickWallSideMaterial(materials, classification, shadeVariant = 'normal') {
  const isInside = classification === 'inside';
  if (shadeVariant === 'dark') {
    return isInside ? materials.wallInsideDark : materials.wallOutsideDark;
  }
  return isInside ? materials.wallInside : materials.wallOutside;
}

/**
 * 3D wall faces use real lighting for depth; do not apply 2D chain shade darkening.
 * @returns {'normal'}
 */
export function resolveWallShadeVariantFor3D() {
  return 'normal';
}

/**
 * Create a per-room floor material (cloned map when textures loaded).
 * @param {FloorPlanMaterialSet} materials
 * @param {string | undefined} floorColor
 * @returns {THREE.MeshStandardMaterial}
 */
export function createRoomFloorMaterial(materials, floorColor) {
  const fallbackColor =
    typeof floorColor === 'string' && /^#[0-9A-Fa-f]{6}$/.test(floorColor.trim())
      ? floorColor.trim()
      : FLOOR_COLOR;

  if (!materials.loaded) {
    return createFloorSurfaceMaterial({ colorHex: fallbackColor });
  }

  return materials.floor;
}

/**
 * @param {THREE.Material[]} materials
 */
export function disposeFloorPlanMaterials(materials) {
  const seen = new Set();
  for (const mat of materials) {
    if (!mat || seen.has(mat)) continue;
    seen.add(mat);
    if (mat instanceof THREE.MeshStandardMaterial && mat.map) {
      mat.map.dispose();
    }
    mat.dispose?.();
  }
}
